import { useEffect, useRef, useState } from 'react';
import {
  BarChart3,
  CalendarDays,
  Inbox,
  ListTodo,
  LogOut,
  MoreHorizontal,
  Receipt,
  Settings,
  Sun,
  Users,
} from 'lucide-react';
import { AnimatePresence } from 'motion/react';

import { BottomTabBar } from '@/components/BottomTabBar';
import { BrandLogo } from '@/components/BrandLogo';
import { CaptureFab } from '@/components/CaptureFab';
import { ContextMenu, type ContextMenuItem } from '@/components/ContextMenu';
import { MoreNavSheet } from '@/components/MoreNavSheet';
import { Sheet } from '@/components/Sheet';
import { Toaster } from '@/components/Toaster';
import { cn } from '@/lib/cn';
import {
  OVERFLOW_VIEWS,
  PRIMARY_VIEWS,
  VIEW_LABELS,
  resolveView,
  type View,
} from '@/lib/views';
import { LoginPage } from '@/features/auth/LoginPage';
import { useCurrentUser, useLogout } from '@/features/auth/useAuth';
import { PlanPage } from '@/features/calendars/PlanPage';
import { SettingsPage } from '@/features/calendars/SettingsPage';
import { BillingPage } from '@/features/billing/BillingPage';
import { ClientsPage } from '@/features/clients/ClientsPage';
import { InboxPage } from '@/features/inbox/InboxPage';
import { CapturePage } from '@/features/capture/CapturePage';
import { usePendingReviewCount } from '@/features/inbox/useBraindump';
import { BacklogPage } from '@/features/tasks/BacklogPage';
import { DailyPage } from '@/features/today/DailyPage';
import { FocusBlockPage } from '@/features/time/FocusBlockPage';
import { FocusBar } from '@/features/time/FocusBar';
import { WorkBlockBar } from '@/features/time/WorkBlockBar';
import { ReportsPage } from '@/features/time/ReportsPage';
import { useFocus } from '@/features/time/useFocus';
import { useIsCompact } from '@/lib/useMediaQuery';

function wantsCaptureFromUrl(): boolean {
  return new URLSearchParams(window.location.search).get('view') === 'capture';
}

function resolveShellView(search: string): View {
  const view = resolveView(search);
  // Capture is a sheet, not a destination, land on Daily underneath.
  return view === 'capture' ? 'daily' : view;
}

export function App() {
  const { data: user, isPending } = useCurrentUser();

  if (isPending) {
    return (
      <main className="flex min-h-dvh items-center justify-center p-5">
        <div className="h-32 w-full max-w-sm animate-pulse rounded-lg bg-surface-2" />
      </main>
    );
  }

  if (!user) return <LoginPage />;

  return <SignedIn />;
}

const WORK_BLOCK_MIN_KEY = 'chronos.work-block-minimized-id';
/** Tab row height (matches BottomTabBar); safe-area is added separately. */
const TAB_BAR_ROW = '3.25rem';
const FOCUS_BAR_ROW = '3.75rem';

function SignedIn() {
  const compact = useIsCompact();
  const logout = useLogout();
  const pendingReview = usePendingReviewCount();
  const [view, setView] = useState<View>(() => resolveShellView(window.location.search));
  const [captureOpen, setCaptureOpen] = useState(wantsCaptureFromUrl);
  const [moreOpen, setMoreOpen] = useState(false);
  const focus = useFocus();
  const activeBlockId = focus.data?.active_work_block_id ?? null;
  const [workBlockMinimized, setWorkBlockMinimized] = useState(() => {
    const stored = localStorage.getItem(WORK_BLOCK_MIN_KEY);
    return stored != null && stored !== '';
  });

  useEffect(() => {
    if (!activeBlockId) {
      setWorkBlockMinimized(false);
      localStorage.removeItem(WORK_BLOCK_MIN_KEY);
      return;
    }
    const stored = localStorage.getItem(WORK_BLOCK_MIN_KEY);
    setWorkBlockMinimized(stored === String(activeBlockId));
  }, [activeBlockId]);

  const minimizeWorkBlock = () => {
    if (!activeBlockId) return;
    setWorkBlockMinimized(true);
    localStorage.setItem(WORK_BLOCK_MIN_KEY, String(activeBlockId));
  };

  const expandWorkBlock = () => {
    setWorkBlockMinimized(false);
    localStorage.removeItem(WORK_BLOCK_MIN_KEY);
  };

  const exitWorkBlock = () => {
    setWorkBlockMinimized(false);
    localStorage.removeItem(WORK_BLOCK_MIN_KEY);
    void focus.refetch();
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (captureOpen) {
      params.set('view', 'capture');
    } else if (view === 'daily') {
      params.delete('view');
    } else {
      params.set('view', view);
    }
    if (view !== 'settings') {
      params.delete('settings');
    }
    if (view !== 'reports') {
      params.delete('tab');
    }
    // Keep share text while capture is open; drop it when closed.
    if (!captureOpen) {
      params.delete('text');
    }
    const qs = params.toString();
    const next = qs ? `/?${qs}` : '/';
    if (`${window.location.pathname}${window.location.search}` !== next) {
      window.history.replaceState(null, '', next);
    }
  }, [view, captureOpen]);

  // Each view owns its own scroll position.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [view]);

  const showWorkBlockFullscreen = activeBlockId && !workBlockMinimized;
  const showWorkBlockBar = Boolean(activeBlockId && workBlockMinimized);
  // Match FocusBar visibility: Pomodoro session OR tracking-only timer.
  const focusState = focus.data;
  const timerOpen =
    Boolean(focusState?.task_started_at) || (focusState?.task_elapsed_seconds ?? 0) > 0;
  const showFocusBar =
    !activeBlockId &&
    Boolean(focusState && (focusState.mode !== 'idle' || timerOpen));
  const showFocusChrome = showWorkBlockBar || showFocusBar;

  const showTabBar = compact && !showWorkBlockFullscreen;
  const tabBarHeight = showTabBar
    ? `calc(${TAB_BAR_ROW} + env(safe-area-inset-bottom, 0px))`
    : '0px';
  const focusChromeHeight = showFocusChrome ? FOCUS_BAR_ROW : '0px';
  // Desktop focus bar needs the home-indicator gap; mobile tabs already own it.
  const focusBarPad = showTabBar
    ? '0.5rem'
    : 'max(0.5rem, env(safe-area-inset-bottom, 0px))';
  const focusSafeExtra = showFocusChrome && !showTabBar
    ? 'env(safe-area-inset-bottom, 0px)'
    : '0px';
  const chromeBottom = `calc(${tabBarHeight} + ${focusChromeHeight} + ${focusSafeExtra})`;

  const closeCapture = () => setCaptureOpen(false);
  const finishCapture = () => {
    setCaptureOpen(false);
    setView('inbox');
  };

  return (
    <div
      className="min-h-dvh"
      style={
        {
          ['--tab-bar-height' as string]: tabBarHeight,
          ['--focus-chrome-height' as string]: focusChromeHeight,
          ['--focus-bar-pad' as string]: focusBarPad,
          ['--chrome-bottom' as string]: chromeBottom,
        } as React.CSSProperties
      }
    >
      {!showWorkBlockFullscreen && (
        <TopBar view={view} onChangeView={setView} />
      )}
      <main className={cn((showFocusChrome || showTabBar) && 'pb-[var(--chrome-bottom)]')}>
        {view === 'daily' && (
          <DailyPage
            onOpenPlan={() => setView('plan')}
            onExpandWorkBlock={expandWorkBlock}
          />
        )}
        {view === 'backlog' && <BacklogPage />}
        {view === 'plan' && <PlanPage />}
        {view === 'inbox' && <InboxPage />}
        {view === 'clients' && <ClientsPage />}
        {view === 'reports' && <ReportsPage />}
        {view === 'billing' && <BillingPage />}
        {view === 'settings' && <SettingsPage />}
      </main>

      {!activeBlockId && <FocusBar />}

      {showWorkBlockBar && activeBlockId && (
        <WorkBlockBar blockId={activeBlockId} onExpand={expandWorkBlock} />
      )}

      {showWorkBlockFullscreen && activeBlockId && (
        <FocusBlockPage
          blockId={activeBlockId}
          onExit={exitWorkBlock}
          onMinimize={minimizeWorkBlock}
        />
      )}

      {showTabBar && (
        <>
          {!captureOpen && <CaptureFab onClick={() => setCaptureOpen(true)} />}
          <BottomTabBar
            view={view}
            onChangeView={setView}
            onOpenMore={() => setMoreOpen(true)}
            inboxBadge={pendingReview > 0}
            inboxBadgeLabel={
              pendingReview > 0 ? `${pendingReview} waiting for review` : undefined
            }
            moreActive={OVERFLOW_VIEWS.includes(view)}
          />
        </>
      )}

      <MoreNavSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        activeView={view}
        onSelectView={setView}
        onSignOut={() => logout.mutate()}
      />

      <AnimatePresence>
        {captureOpen && (
          <Sheet title="Capture" onClose={closeCapture}>
            <CapturePage embedded onDone={finishCapture} />
          </Sheet>
        )}
      </AnimatePresence>

      <Toaster />
    </div>
  );
}

function TopBar({
  view,
  onChangeView,
}: {
  view: View;
  onChangeView: (view: View) => void;
}) {
  const logout = useLogout();
  const pendingReview = usePendingReviewCount();

  const moreRef = useRef<HTMLButtonElement>(null);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);

  const inOverflow = OVERFLOW_VIEWS.includes(view);

  const toggleMenu = () => {
    if (menuAt) {
      setMenuAt(null);
      return;
    }
    const rect = moreRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuAt({ x: rect.left, y: rect.bottom + 4 });
  };

  const menuItems: ContextMenuItem[] = [
    {
      key: 'clients',
      label: 'Clients',
      icon: Users,
      onSelect: () => onChangeView('clients'),
    },
    {
      key: 'reports',
      label: 'Reports',
      icon: BarChart3,
      onSelect: () => onChangeView('reports'),
    },
    {
      key: 'billing',
      label: 'Billing',
      icon: Receipt,
      onSelect: () => onChangeView('billing'),
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: Settings,
      onSelect: () => onChangeView('settings'),
    },
    {
      key: 'logout',
      label: 'Sign out',
      icon: LogOut,
      tone: 'danger',
      onSelect: () => logout.mutate(),
    },
  ];

  return (
    <>
      <header className="material sticky top-0 z-chrome border-b pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-2 px-4 py-2 sm:px-5">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <button
              type="button"
              onClick={() => onChangeView('daily')}
              aria-label="Go to Daily"
              className="pressable shrink-0 rounded"
            >
              <BrandLogo size="xs" decorative />
            </button>

            {/* Mobile: current page title */}
            <p className="min-w-0 flex-1 truncate text-body font-medium text-text sm:hidden">
              {VIEW_LABELS[view]}
            </p>

            {/* Desktop: four primary tabs + More for the rest */}
            <nav className="hidden min-w-0 flex-1 items-center gap-0.5 sm:flex">
              {PRIMARY_VIEWS.map((id) => (
                <NavItem
                  key={id}
                  active={view === id}
                  onClick={() => onChangeView(id)}
                  icon={
                    id === 'daily' ? (
                      <Sun size={15} strokeWidth={1.5} aria-hidden />
                    ) : id === 'plan' ? (
                      <CalendarDays size={15} strokeWidth={1.5} aria-hidden />
                    ) : id === 'backlog' ? (
                      <ListTodo size={15} strokeWidth={1.5} aria-hidden />
                    ) : (
                      <Inbox size={15} strokeWidth={1.5} aria-hidden />
                    )
                  }
                  label={VIEW_LABELS[id]}
                  badge={id === 'inbox' && pendingReview > 0}
                  badgeLabel={
                    id === 'inbox' && pendingReview > 0
                      ? `${pendingReview} waiting for review`
                      : undefined
                  }
                />
              ))}
            </nav>
          </div>

          {/* Desktop: overflow menu */}
          <button
            ref={moreRef}
            type="button"
            onClick={toggleMenu}
            aria-haspopup="menu"
            aria-expanded={menuAt !== null}
            aria-label="More"
            aria-current={inOverflow ? 'page' : undefined}
            className={cn(
              'pressable hidden size-touch shrink-0 items-center justify-center rounded sm:inline-flex',
              'transition-colors duration-press',
              inOverflow ? 'bg-surface-2 text-text' : 'text-text-secondary hover:text-text',
            )}
          >
            <MoreHorizontal size={18} strokeWidth={1.5} aria-hidden />
          </button>
        </div>
      </header>

      {menuAt && (
        <ContextMenu
          x={menuAt.x}
          y={menuAt.y}
          label="More"
          items={menuItems}
          triggerRef={moreRef}
          onClose={() => setMenuAt(null)}
        />
      )}
    </>
  );
}

function NavItem({
  active,
  onClick,
  icon,
  label,
  badge,
  badgeLabel,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: boolean;
  badgeLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'pressable inline-flex min-h-touch shrink-0 items-center gap-1.5 rounded px-2 text-small sm:gap-2 sm:px-3',
        'transition-colors duration-press',
        badge && 'pr-2 sm:pr-3',
        active ? 'bg-surface-2 font-medium text-text' : 'text-text-secondary hover:text-text',
      )}
    >
      {icon}
      {label}
      {badge && (
        <span aria-hidden className="size-dot shrink-0 rounded-full bg-accent" />
      )}
      {badge && badgeLabel && <span className="sr-only">, {badgeLabel}</span>}
    </button>
  );
}
